import { Container, Graphics, Text } from "pixi.js";
import type { PremiumType } from "../../../shared/types";
import { PREMIUM_COLORS } from "../../../shared/constants";

export class BoardSquare extends Container {
  private bg: Graphics;
  private _label: Text;
  private premiumLabel: Text;
  private _onClick?: (x: number, y: number) => void;
  private isBlack = false;
  private isOpponentSolved = false;
  private isClueHighlighted = false;
  private currentPremium: PremiumType | null = null;
  private _clueNumberText: Text | null = null;

  constructor(
    public gridX: number,
    public gridY: number,
    private cellSize: number
  ) {
    super();

    this.bg = new Graphics();
    this.addChild(this.bg);

    this._label = new Text({
      text: "",
      style: { fontSize: 14, fill: 0xcccccc, fontFamily: "monospace" },
    });
    this._label.anchor.set(0.5);
    this._label.x = cellSize / 2;
    this._label.y = cellSize / 2;
    this.addChild(this._label);

    this.premiumLabel = new Text({
      text: "",
      style: { fontSize: 9, fill: 0xffffff, fontFamily: "monospace" },
    });
    this.premiumLabel.anchor.set(0.5);
    this.premiumLabel.x = cellSize / 2;
    this.premiumLabel.y = cellSize / 2;
    this.addChild(this.premiumLabel);

    this.eventMode = "static";
    this.cursor = "pointer";
    this.on("pointerdown", () => {
      if (!this.isBlack && this._onClick) this._onClick(this.gridX, this.gridY);
    });

    this.render();
  }

  private render() {
    // Remove old bg and create new one to avoid PixiJS clear() issues
    const oldBg = this.bg;
    this.bg = new Graphics();

    if (this.isBlack) {
      this.bg.rect(0, 0, this.cellSize, this.cellSize);
      this.bg.fill({ color: 0x111111 });
      this.bg.stroke({ width: 1, color: 0x222222 });
      this._label.text = "";
      this.premiumLabel.text = "";
      this.cursor = "default";
      this.eventMode = "none";
    } else {
      this.eventMode = "static";
      this.cursor = "pointer";

      if (this.currentPremium) {
        const color = PREMIUM_COLORS[this.currentPremium];
        this.bg.rect(0, 0, this.cellSize, this.cellSize);
        this.bg.fill({ color: Number(`0x${color.slice(1)}`) });
        if (this.isClueHighlighted) {
          this.bg.stroke({ width: 4, color: 0xf27405 });
        } else if (this.isOpponentSolved) {
          // Opponent solved a word through this premium square: green outline
          this.bg.stroke({ width: 3, color: 0x22c55e });
        } else {
          this.bg.stroke({ width: 1, color: 0xffffff, alpha: 0.3 });
        }
        this.premiumLabel.text = this.currentPremium;
      } else {
        // Light crossword-square color so open spaces are clearly visible
        this.bg.rect(0, 0, this.cellSize, this.cellSize);
        this.bg.fill({ color: this.isOpponentSolved ? 0x86efac : 0xe8e8f0 });

        if (this.isClueHighlighted) {
          this.bg.stroke({ width: 4, color: 0xf27405 });
        } else {
          const strokeColor = this.isOpponentSolved ? 0x22c55e : 0x3a3a5a;
          const strokeWidth = this.isOpponentSolved ? 3 : 1;
          this.bg.stroke({ width: strokeWidth, color: strokeColor });
        }
        this.premiumLabel.text = "";
      }
    }

    this.addChildAt(this.bg, 0);
    if (oldBg) {
      this.removeChild(oldBg);
      oldBg.destroy();
    }
  }

  set onCellClick(cb: ((x: number, y: number) => void) | undefined) {
    this._onClick = cb;
  }

  set cellLabel(text: string) {
    this._label.text = text;
  }

  set clueNumber(num: number) {
    // Remove old clue number if present
    if (this._clueNumberText) {
      this.removeChild(this._clueNumberText);
      this._clueNumberText.destroy();
      this._clueNumberText = null;
    }
    // Small number in top-left corner for crossword clues
    const clueText = new Text({
      text: String(num),
      style: { fontSize: 10, fill: 0x555555, fontFamily: "monospace", fontWeight: "bold" },
    });
    clueText.anchor.set(0, 0);
    clueText.x = 2;
    clueText.y = 1;
    this.addChild(clueText);
    this._clueNumberText = clueText;
  }

  setBlack() {
    if (this.isBlack) return;
    this.isBlack = true;
    // Remove clue number from black squares
    if (this._clueNumberText) {
      this.removeChild(this._clueNumberText);
      this._clueNumberText.destroy();
      this._clueNumberText = null;
    }
    this.render();
  }

  setPremium(type: PremiumType) {
    if (this.isBlack) return;
    this.currentPremium = type;
    this.render();
  }

  highlight(active: boolean) {
    if (this.isClueHighlighted === active) return;
    this.isClueHighlighted = active;
    this.render();
  }

  setOpponentSolved(isSolved: boolean) {
    if (this.isBlack) return;
    this.isOpponentSolved = isSolved;
    this.render();
  }

  get premiumType(): PremiumType | null {
    return this.currentPremium;
  }
}
